// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId, useMemo, useState, type CSSProperties, type KeyboardEvent } from 'react';

import type { Sample, SurveyFractions, SurveyType } from '../../../api/types';
import { tokenVar } from '../../../bygning/lens';
import {
  FLOW_EMPTY,
  FLOW_GEOMETRY,
  flowAria,
  flowData,
  flowInfo,
  flowLabel,
  flowLayout,
  flowMatch,
  flowWhy,
  omittedNote,
  type FlowNode,
} from '../../../bygning/materialflow';
import { formatTonnes } from '../../../kortlaegning/vocab';
import styles from './MaterialFlow.module.css';

export interface MaterialFlowProps {
  types: readonly SurveyType[];
  samples: readonly Sample[];
  fractions: SurveyFractions | null;
  /** A type node: open the type in Ressourcer. */
  onOpenType: (typeId: number) => void;
  /** A treatment node: the pkt. 10 table under the flow. */
  onShowFractions: () => void;
  /** The Blokeret node: "Det, der blokerer" (Gennemgang). */
  onShowBlocking: () => void;
}

/** The ribbon's and the node's colour, read by the CSS (`--flow-color`). */
function colorStyle(token: string): CSSProperties {
  return { '--flow-color': tokenVar(token) } as CSSProperties;
}

/**
 * "Materialeflow · pkt. 10" (prototype Variant C): a Sankey of the tonnes
 * from each type to its treatment, or to Blokeret while it blocks. Hovering
 * or focusing a node or ribbon dims the others and says what it carries (a
 * polite live line); a node is a link — a type opens it, a treatment goes to
 * the fraction table, Blokeret to what blocks. On a phone the drawing gives
 * way to a table, which is also the text alternative everywhere. The data and
 * geometry are `bygning/materialflow.ts`; no particles, no chart library.
 */
export function MaterialFlow({ types, samples, fractions, onOpenType, onShowFractions, onShowBlocking }: MaterialFlowProps) {
  const id = useId();
  const fd = useMemo(() => flowData(types, samples, fractions), [types, samples, fractions]);
  const layout = useMemo(() => flowLayout(fd), [fd]);
  // The pointer and the keyboard each keep their own: leaving with the mouse
  // does not clear what a focused node shows.
  const [hover, setHover] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const active = hover ?? focused;
  const note = omittedNote(fd);

  const activate = (n: FlowNode) => {
    if (n.typeId !== undefined) onOpenType(n.typeId);
    else if (n.target === 'blocked') onShowBlocking();
    else onShowFractions();
  };
  const onKey = (n: FlowNode) => (e: KeyboardEvent<SVGGElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    activate(n);
  };
  const hoverProps = (key: string) => ({
    onMouseEnter: () => setHover(key),
    onMouseLeave: () => setHover((k) => (k === key ? null : k)),
  });

  return (
    <section className={styles.flow} aria-labelledby={`${id}-h`}>
      <div className={styles.head}>
        <h3 id={`${id}-h`} className={styles.heading}>
          Materialeflow · pkt. 10
        </h3>
        <span className={styles.side}>Del 2</span>
      </div>
      {fd.rows.length === 0 ? (
        <p className={styles.note}>{FLOW_EMPTY}</p>
      ) : (
        <>
          <p className={styles.note}>
            Tons fra bygningsdele til behandling. Bevaring bliver i bygningen.
            <span className={styles.pointer}> Klik en bygningsdel for at åbne typen.</span>
          </p>
          <svg
            className={styles.svg}
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            role="group"
            aria-label={flowAria(fd)}
            onMouseLeave={() => setHover(null)}
          >
            <g>
              {layout.ribbons.map((r) => {
                const cls = [styles.ribbon, r.blocked && styles.blocked, !flowMatch(active, r) && styles.dim].filter(Boolean).join(' ');
                return (
                  <g key={r.key} style={colorStyle(r.colorToken)} {...hoverProps(r.key)}>
                    <path className={cls} d={r.d} strokeWidth={r.strokeWidth.toFixed(1)} />
                    {r.blocked && <path className={styles.dash} d={r.d} />}
                  </g>
                );
              })}
            </g>
            <g>
              {layout.nodes.map((n) => (
                <g
                  key={n.key}
                  className={styles.node}
                  style={colorStyle(n.colorToken)}
                  role="link"
                  tabIndex={0}
                  aria-label={n.aria}
                  onClick={() => activate(n)}
                  onKeyDown={onKey(n)}
                  onFocus={() => setFocused(n.key)}
                  onBlur={() => setFocused((k) => (k === n.key ? null : k))}
                  {...hoverProps(n.key)}
                >
                  <title>{n.title}</title>
                  <rect
                    className={n.side === 'left' ? `${styles.rect} ${styles.typeRect}` : styles.rect}
                    x={n.x}
                    y={n.y.toFixed(1)}
                    width={FLOW_GEOMETRY.nodeWidth}
                    height={n.rectH.toFixed(1)}
                  />
                  <text className={styles.label} x={n.textX} y={n.labelY.toFixed(1)} textAnchor={n.anchor}>
                    {n.label}
                  </text>
                  <text className={styles.sub} x={n.textX} y={n.subY.toFixed(1)} textAnchor={n.anchor}>
                    {n.sub}
                  </text>
                </g>
              ))}
            </g>
          </svg>
          {/* The drawing as a table: shown on a phone, read out everywhere. */}
          <table className={styles.table}>
            <caption className={styles.srOnly}>Materialeflow som tabel</caption>
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col">Til</th>
                <th scope="col" className={styles.num}>
                  Tons
                </th>
              </tr>
            </thead>
            <tbody>
              {fd.rows.map((r) => (
                <tr key={r.typeId}>
                  <td>{r.name}</td>
                  <td>
                    {flowLabel(r.to)}
                    {r.to === 'blocked' && <span className={styles.why}> · {flowWhy(r)}</span>}
                  </td>
                  <td className={`${styles.num} mono`}>{formatTonnes(r.tonnes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.foot}>
            <span>
              <b className={styles.figure}>{formatTonnes(fd.ready)}</b> klar
            </span>
            <span>
              <b className={`${styles.figure} ${styles.crit}`}>{formatTonnes(fd.blocked)}</b> blokeret
            </span>
            <span className={styles.info} aria-live="polite">
              {flowInfo(fd, active)}
            </span>
          </div>
          {note && <p className={styles.note}>{note}</p>}
        </>
      )}
    </section>
  );
}
